import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { createVirtualTryOn, fetchProduct, fetchProducts } from "@/lib/api";
import { useStore } from "@/contexts/StoreContext";
import {
    getInitialCustomAmount,
    getMaximumCustomAmount,
    getMinimumCustomAmount,
    isCustomPricedProduct,
} from "@/lib/pricing";
import {
    Heart,
    Truck,
    Shield,
    RotateCcw,
    ChevronLeft,
    LoaderCircle,
    Upload,
    Droplets,
    Wind,
    Sun,
} from "lucide-react";
import ProductCard from "@/components/ProductCard";
import { toast } from "sonner";

const TRY_ON_CACHE_NAME = "uknitwear-try-on-garments-v1";
const TRY_ON_SETTINGS = [
    { value: "white-background", label: "White Background" },
    { value: "beach", label: "Beach" },
    { value: "field", label: "Field" },
    { value: "city", label: "City" },
    { value: "night", label: "Night" },
    { value: "party", label: "Party" },
];

const toAbsoluteImageUrl = (imagePath) => {
    if (!imagePath) return "";
    return new URL(imagePath, window.location.origin).toString();
};

const buildCachedGarmentFile = async (product) => {
    const heroImage = product?.images?.[0];
    if (!heroImage) {
        throw new Error("This piece is missing its reference image.");
    }

    const imageUrl = toAbsoluteImageUrl(heroImage);
    const cacheKey = imageUrl;

    if ("caches" in window) {
        const cache = await caches.open(TRY_ON_CACHE_NAME);
        let response = await cache.match(cacheKey);

        if (!response) {
            response = await fetch(imageUrl);
            if (!response.ok) {
                throw new Error("The garment image could not be cached.");
            }
            await cache.put(cacheKey, response.clone());
        }

        const blob = await response.blob();
        const extension = heroImage.split(".").pop()?.split("?")[0] || "png";
        return new File([blob], `${product.id}-garment.${extension}`, {
            type: blob.type || "image/png",
        });
    }

    const response = await fetch(imageUrl);
    if (!response.ok) {
        throw new Error("The garment image could not be loaded.");
    }

    const blob = await response.blob();
    const extension = heroImage.split(".").pop()?.split("?")[0] || "png";
    return new File([blob], `${product.id}-garment.${extension}`, {
        type: blob.type || "image/png",
    });
};

const formatCategoryLabel = (category) => {
    if (!category) return "piece";
    const labels = {
        tops: "layering piece",
        bottoms: "base layer",
        outerwear: "outer layer",
        footwear: "finish",
        accessories: "accent",
    };
    return labels[category] || "piece";
};

const PRODUCT_INFO_LINKS = [
    { label: "Care", to: "/care" },
    { label: "Size Guide", to: "/size-guide" },
    { label: "Shipping", to: "/shipping" },
    { label: "FAQ", to: "/faq" },
];

const ProductDetail = () => {
    const { id } = useParams();
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [related, setRelated] = useState([]);
    const [size, setSize] = useState("");
    const [color, setColor] = useState("");
    const [activeImg, setActiveImg] = useState(0);
    const [tryOnPhoto, setTryOnPhoto] = useState(null);
    const [tryOnPreview, setTryOnPreview] = useState("");
    const [tryOnSetting, setTryOnSetting] = useState("white-background");
    const [tryOnResult, setTryOnResult] = useState("");
    const [tryOnLoading, setTryOnLoading] = useState(false);
    const [customAmount, setCustomAmount] = useState(100);
    const { addToCart, toggleWishlist, inWishlist } = useStore();

    useEffect(() => {
        let cancelled = false;

        setLoading(true);
        setProduct(null);
        setRelated([]);
        setActiveImg(0);
        setSize("");
        setColor("");
        setTryOnPhoto(null);
        setTryOnPreview("");
        setTryOnSetting("white-background");
        setTryOnResult("");
        setCustomAmount(100);
        window.scrollTo(0, 0);

        fetchProduct(id)
            .then((p) => {
                if (cancelled) return;

                setProduct(p);
                setColor(p?.colors?.[0] || "");
                setSize(p?.sizes?.[0] || "");
                if (p) {
                    setCustomAmount(getInitialCustomAmount(p));
                }

                if (!p) {
                    setRelated([]);
                    return;
                }

                fetchProducts({ category: p.category }).then((all) => {
                    if (cancelled) return;
                    setRelated(all.filter((x) => x.id !== p.id).slice(0, 4));
                });
            })
            .catch(() => {
                if (cancelled) return;
                setProduct(null);
                setRelated([]);
            })
            .finally(() => {
                if (cancelled) return;
                setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [id]);

    useEffect(() => {
        if (!product?.images?.[0]) {
            return undefined;
        }

        buildCachedGarmentFile(product).catch(() => {
            // Best-effort cache warmup; submission retries this path if needed.
        });

        return undefined;
    }, [product]);

    useEffect(() => {
        if (!tryOnPhoto) {
            setTryOnPreview("");
            return undefined;
        }

        const objectUrl = URL.createObjectURL(tryOnPhoto);
        setTryOnPreview(objectUrl);

        return () => {
            URL.revokeObjectURL(objectUrl);
        };
    }, [tryOnPhoto]);

    if (loading) {
        return (
            <div className="p-20 text-center font-mono-ui text-xs uppercase tracking-[0.25em] text-muted-foreground">
                Loading piece…
            </div>
        );
    }

    if (!product) {
        return (
            <div className="border-b-4 border-black px-4 py-20 text-center md:px-8" data-testid="product-not-found">
                <p className="font-heading text-4xl md:text-6xl uppercase leading-[0.9]">
                    Piece Not Found
                </p>
                <p className="mt-4 font-mono-ui text-xs uppercase tracking-[0.2em] text-muted-foreground">
                    This product may have been removed or the link is out of date.
                </p>
                <Link
                    to="/shop"
                    className="mt-8 inline-flex items-center gap-2 border-2 border-black px-6 py-3 font-mono-ui text-xs uppercase tracking-[0.25em] transition-colors hover:bg-black hover:text-white"
                >
                    <ChevronLeft className="w-4 h-4" /> Back To Shop
                </Link>
            </div>
        );
    }

    const handleAdd = () => {
        const minAmount = getMinimumCustomAmount(product);
        const maxAmount = getMaximumCustomAmount(product);
        const normalizedAmount = Math.round(Number(customAmount) * 100) / 100;

        if (!Number.isFinite(normalizedAmount) || normalizedAmount < minAmount || normalizedAmount > maxAmount) {
            toast.error(`Choose an amount between $${minAmount.toFixed(2)} and $${maxAmount.toFixed(2)}.`);
            return;
        }

        if (!size) {
            toast.error("Select a size first.");
            return;
        }

        addToCart(
            product,
            size || product.sizes?.[0] || "OS",
            color || product.colors?.[0] || "Logo",
            1,
            isCustomPricedProduct(product) ? { customAmount: normalizedAmount } : {}
        );
        toast.success(`${product.name} for $${normalizedAmount.toFixed(2)} added to bag.`);
    };

    const wished = inWishlist(product.id);
    const displayedPrice = isCustomPricedProduct(product) ? customAmount : product.price;
    const minimumCustomAmount = getMinimumCustomAmount(product);
    const maximumCustomAmount = getMaximumCustomAmount(product);
    const outfitDescriptionAccent = color || product.colors?.[0] || "assorted tones";
    const outfitDescriptionLabel = formatCategoryLabel(product.category);
    const outfitDescriptionHighlights = [
        { Icon: Droplets, label: "Cold Wash Care" },
        { Icon: Wind, label: "Air Dry Recommended" },
        { Icon: Sun, label: `Built For ${product.drop}` },
    ];

    const handleTryOnUpload = (event) => {
        const file = event.target.files?.[0];
        event.target.value = "";

        if (!file) {
            return;
        }

        if (!file.type.startsWith("image/")) {
            toast.error("Upload a JPG, PNG, WebP, or similar image file.");
            return;
        }

        setTryOnPhoto(file);
        setTryOnResult("");
    };

    const handleTryOnSubmit = async () => {
        if (!tryOnPhoto) {
            toast.error("Upload a full-body photo first.");
            return;
        }

        setTryOnLoading(true);

        try {
            const garmentImage = await buildCachedGarmentFile(product);
            const response = await createVirtualTryOn({
                personImage: tryOnPhoto,
                garmentImage,
                productName: product.name,
                setting: tryOnSetting,
            });

            setTryOnResult(response.image_url);
            toast.success("Your virtual try-on is ready.");
        } catch (error) {
            let message = error?.response?.data?.detail;

            if (!message && error?.response?.status === 404) {
                message = "The app could not reach the virtual try-on API. Restart the frontend after this update and make sure the backend is running.";
            }

            if (!message && error?.request && !error?.response) {
                message = "The browser could not reach the backend. Make sure the FastAPI server is running and then try again.";
            }

            if (!message) {
                message = error?.message || "Virtual try-on failed. Please try again.";
            }

            toast.error(message);
        } finally {
            setTryOnLoading(false);
        }
    };

    return (
        <div data-testid="product-detail-page">
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 border-b-2 border-dotted border-black/45 px-4 py-3 font-mono-ui text-[10px] uppercase tracking-[0.25em] md:px-8">
                <Link to="/shop" className="hover:text-hazard inline-flex items-center gap-1" data-testid="breadcrumb-shop">
                    <ChevronLeft className="w-3 h-3" /> Shop
                </Link>
                <span className="text-muted-foreground">/</span>
                <Link to={`/shop/${product.category}`} className="hover:text-hazard">
                    {product.category}
                </Link>
                <span className="text-muted-foreground">/</span>
                <span className="text-muted-foreground truncate">{product.name}</span>
            </div>

            {/* Main */}
            <section className="grid grid-cols-1 border-b-4 border-black lg:grid-cols-12">
                {/* Gallery */}
                <div className="border-black lg:col-span-7 lg:border-r-4">
                    <div className="aspect-square overflow-hidden border-b-2 border-black bg-white">
                        <img
                            src={product.images[activeImg]}
                            alt={product.name}
                            className="w-full h-full object-contain object-top p-6"
                            data-testid="product-active-image"
                        />
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4">
                        {product.images.map((img, i) => (
                            <button
                                key={i}
                                type="button"
                                onClick={() => setActiveImg(i)}
                                className={`aspect-square overflow-hidden border-b-2 border-r-2 border-black bg-white md:[&:nth-child(4n)]:border-r-0 [&:nth-last-child(-n+2)]:border-b-0 md:[&:nth-last-child(-n+4)]:border-b-0 ${
                                    activeImg === i ? "ring-4 ring-hazard ring-inset" : ""
                                }`}
                                data-testid={`product-thumb-${i}`}
                            >
                                <img
                                    src={img}
                                    alt={`thumbnail ${i}`}
                                    className="w-full h-full object-contain object-top p-2"
                                />
                            </button>
                        ))}
                    </div>
                </div>

                {/* Info */}
                <div className="flex min-w-0 flex-col p-6 md:p-10 lg:col-span-5 lg:p-12">
                    <div className="mb-3 flex flex-wrap items-center gap-3">
                        <span className="font-mono-ui text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
                            {product.drop} / {product.category}
                        </span>
                        {product.badge && (
                            <span className="bg-hazard text-black px-2 py-0.5 font-mono-ui text-[10px] tracking-[0.2em]">
                                {product.badge}
                            </span>
                        )}
                    </div>
                    <h1
                        className="break-words font-heading text-4xl uppercase leading-[0.9] md:text-6xl"
                        data-testid="product-title"
                    >
                        {product.name}
                    </h1>
                    <div className="mt-4 flex flex-wrap items-baseline gap-3">
                        <span
                            className="font-mono-ui text-2xl"
                            data-testid="product-price"
                        >
                            {isCustomPricedProduct(product) ? `From $${minimumCustomAmount.toFixed(2)}` : `$${product.price.toFixed(2)}`}
                        </span>
                        <span className="font-mono-ui text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                            {isCustomPricedProduct(product)
                                ? `Choose any amount from $${minimumCustomAmount.toFixed(2)} to $${maximumCustomAmount.toFixed(2)}`
                                : "USD / Incl. taxes calc at checkout"}
                        </span>
                    </div>

                    <p className="mt-6 leading-relaxed text-sm md:text-base text-foreground/80">
                        {product.description}
                    </p>

                    {/* Color */}
                    {product.colors?.length > 0 && (
                        <div className="mt-8">
                            <div className="flex justify-between font-mono-ui text-[10px] uppercase tracking-[0.25em] mb-3">
                                <span>Colour</span>
                                <span className="text-muted-foreground">{color}</span>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {product.colors.map((c) => (
                                    <button
                                        key={c}
                                        type="button"
                                        onClick={() => setColor(c)}
                                        className={`border-2 border-black px-3 py-2 font-mono-ui text-[10px] uppercase tracking-[0.2em] transition-colors ${
                                            color === c
                                                ? "bg-black text-white"
                                                : "bg-white text-black hover:bg-black hover:text-white"
                                        }`}
                                        data-testid={`color-option-${c}`}
                                    >
                                        {c}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Size */}
                    <div className="mt-6">
                        <div className="flex justify-between font-mono-ui text-[10px] uppercase tracking-[0.25em] mb-3">
                            <span>Size</span>
                            <button type="button" className="hover:text-hazard">
                                Size Guide ↗
                            </button>
                        </div>
                        <div className="grid grid-cols-5 sm:grid-cols-6 gap-1">
                            {product.sizes.map((s) => (
                                <button
                                    key={s}
                                    type="button"
                                    onClick={() => setSize(s)}
                                    className={`border-2 border-black py-3 font-mono-ui text-xs transition-colors ${
                                        size === s
                                            ? "bg-black text-white"
                                            : "bg-white text-black hover:bg-black hover:text-white"
                                    }`}
                                    data-testid={`size-option-${s}`}
                                >
                                    {s}
                                </button>
                            ))}
                        </div>
                    </div>

                    {isCustomPricedProduct(product) && (
                        <div className="mt-6 border-2 border-black bg-[#f6f0e8] p-4">
                            <div className="flex items-baseline justify-between gap-4">
                                <span className="font-mono-ui text-[10px] uppercase tracking-[0.25em]">
                                    {product.is_voucher ? "Voucher Amount" : "Choose Your Price"}
                                </span>
                                <span className="font-heading text-3xl uppercase">
                                    ${displayedPrice.toFixed(2)}
                                </span>
                            </div>
                            <input
                                type="range"
                                min={minimumCustomAmount}
                                max={maximumCustomAmount}
                                step="1"
                                value={customAmount}
                                onChange={(event) => setCustomAmount(Number(event.target.value))}
                                className="mt-4 w-full accent-black"
                                data-testid="custom-amount-slider"
                            />
                            <input
                                type="number"
                                min={minimumCustomAmount}
                                max={maximumCustomAmount}
                                step="1"
                                value={customAmount}
                                onChange={(event) => setCustomAmount(Number(event.target.value))}
                                className="mt-4 w-full border-2 border-black bg-white px-4 py-3 font-mono-ui text-sm text-black focus:border-hazard focus:outline-none"
                                data-testid="custom-amount-input"
                            />
                            <p className="mt-3 font-mono-ui text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                                {product.is_voucher ? "Select your amount" : "Move the bar or type your price"}
                            </p>
                        </div>
                    )}

                    {/* CTAs */}
                    <div className="mt-8 flex gap-2">
                        <button
                            type="button"
                            onClick={handleAdd}
                            className="flex-1 border-2 border-black bg-black py-4 font-mono-ui text-xs uppercase tracking-[0.25em] text-white transition-colors hover:border-hazard hover:bg-hazard hover:text-black"
                            data-testid="add-to-cart-button"
                        >
                            Add To Bag — ${displayedPrice.toFixed(2)}
                        </button>
                        <button
                            type="button"
                            onClick={() => toggleWishlist(product)}
                            className={`border-2 px-4 transition-colors ${
                                wished
                                    ? "border-hazard bg-hazard text-black"
                                    : "border-black bg-white text-black hover:bg-black hover:text-white"
                            }`}
                            aria-label="Wishlist"
                            data-testid="wishlist-toggle-button"
                        >
                            <Heart className="w-5 h-5" fill={wished ? "currentColor" : "none"} />
                        </button>
                    </div>

                    {/* Trust strip */}
                    <div className="mt-8 grid grid-cols-3 gap-2 border-t-2 border-black pt-6">
                        {[
                            [Truck, "Free Ship $200+"],
                            [RotateCcw, "30-Day Returns"],
                            [Shield, "Authentic"],
                        ].map(([Icon, label]) => (
                            <div key={label} className="flex flex-col items-center gap-1 text-center">
                                <Icon className="w-4 h-4" />
                                <span className="font-mono-ui text-[9px] uppercase tracking-[0.2em] text-muted-foreground">
                                    {label}
                                </span>
                            </div>
                        ))}
                    </div>

                    {!product.is_voucher && (
                        <div className="mt-8 border-2 border-black bg-[#f6f0e8]">
                            <div className="border-b-2 border-black px-4 py-3 font-mono-ui text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                                Outfit Description
                            </div>
                            <div className="px-4 py-5">
                                <div>
                                    <p className="text-sm md:text-base leading-relaxed text-foreground/85">
                                        {product.description}
                                    </p>
                                    <p className="mt-4 font-mono-ui text-[10px] uppercase tracking-[0.2em] leading-5 text-muted-foreground">
                                        {product.drop} editorial {outfitDescriptionLabel} in {outfitDescriptionAccent}, styled for a soft sculptural silhouette and an easy layered finish.
                                    </p>
                                </div>

                                <div className="mt-6 grid gap-3 sm:grid-cols-3">
                                    {outfitDescriptionHighlights.map(({ Icon, label }) => (
                                        <div key={label} className="flex items-start gap-4">
                                            <div className="flex h-14 w-14 shrink-0 items-center justify-center border-2 border-black bg-white">
                                                <Icon className="h-6 w-6" />
                                            </div>
                                            <span className="pt-1 font-mono-ui text-xs uppercase leading-5 tracking-[0.18em] text-foreground/80">
                                                {label}
                                            </span>
                                        </div>
                                    ))}
                                </div>

                                <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2 border-t border-black/20 pt-4">
                                    {PRODUCT_INFO_LINKS.map((item) => (
                                        <Link
                                            key={item.to}
                                            to={item.to}
                                            className="font-mono-ui text-[10px] uppercase tracking-[0.2em] text-black underline-offset-4 hover:text-hazard hover:underline"
                                        >
                                            {item.label}
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </section>

            {!product.is_voucher && (
                <section className="border-b-4 border-black bg-[#f6f0e8]">
                    <div className="border-b-2 border-black px-4 py-10 md:px-8">
                        <span className="font-mono-ui text-xs uppercase tracking-[0.25em] text-muted-foreground">
                            // Virtual Try-On
                        </span>
                        <div className="mt-3 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                            <div>
                                <h2 className="font-heading text-4xl md:text-6xl uppercase leading-none">
                                    See It On You
                                </h2>
                                <p className="mt-3 max-w-3xl text-sm md:text-base text-foreground/80">
                                    Upload a straight-on full-body photo with your face visible and nothing blocking the outfit area. We&apos;ll combine it with this product&apos;s first image and stage it in the setting you choose.
                                </p>
                            </div>
                            <div className="font-mono-ui text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                                Uses the first product image as the garment reference
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-12">
                        <div className="border-b-2 border-black p-4 md:p-8 lg:col-span-5 lg:border-b-0 lg:border-r-2">
                            <div className="grid gap-6">
                                <label className="block">
                                    <span className="font-mono-ui text-[10px] uppercase tracking-[0.25em]">
                                        Upload Your Photo
                                    </span>
                                    <div className="mt-3 border-2 border-dashed border-black bg-white p-5">
                                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                            <div className="flex items-center gap-3">
                                                <div className="flex h-11 w-11 items-center justify-center border-2 border-black bg-[#fff7eb]">
                                                    <Upload className="w-5 h-5" />
                                                </div>
                                                <div>
                                                    <p className="font-heading text-2xl uppercase leading-none">
                                                        Front-Facing Shot
                                                    </p>
                                                    <p className="mt-1 text-xs text-muted-foreground">
                                                        Full body visible, straight posture, clear face.
                                                    </p>
                                                </div>
                                            </div>
                                            <span className="relative inline-flex">
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    className="absolute inset-0 cursor-pointer opacity-0"
                                                    onChange={handleTryOnUpload}
                                                    data-testid="virtual-try-on-upload"
                                                />
                                                <span className="inline-flex items-center justify-center border-2 border-black px-4 py-3 font-mono-ui text-[10px] uppercase tracking-[0.25em] transition-colors hover:bg-black hover:text-white">
                                                    {tryOnPhoto ? "Replace Photo" : "Choose Photo"}
                                                </span>
                                            </span>
                                        </div>

                                        {tryOnPhoto && (
                                            <p className="mt-4 font-mono-ui text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                                                Loaded: {tryOnPhoto.name}
                                            </p>
                                        )}
                                    </div>
                                </label>

                                <div>
                                    <span className="font-mono-ui text-[10px] uppercase tracking-[0.25em]">
                                        Setting
                                    </span>
                                    <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-2">
                                        {TRY_ON_SETTINGS.map((option) => (
                                            <button
                                                key={option.value}
                                                type="button"
                                                onClick={() => setTryOnSetting(option.value)}
                                                className={`border-2 border-black px-3 py-3 font-mono-ui text-[10px] uppercase tracking-[0.2em] transition-colors ${
                                                    tryOnSetting === option.value
                                                        ? "bg-black text-white"
                                                        : "bg-white text-black hover:bg-black hover:text-white"
                                                }`}
                                                data-testid={`try-on-setting-${option.value}`}
                                            >
                                                {option.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <button
                                    type="button"
                                    onClick={handleTryOnSubmit}
                                    disabled={tryOnLoading}
                                    className="inline-flex items-center justify-center gap-2 border-2 border-black bg-black px-5 py-4 font-mono-ui text-xs uppercase tracking-[0.25em] text-white transition-colors hover:border-hazard hover:bg-hazard hover:text-black disabled:cursor-not-allowed disabled:opacity-70"
                                    data-testid="virtual-try-on-submit"
                                >
                                    {tryOnLoading ? (
                                        <>
                                            <LoaderCircle className="w-4 h-4 animate-spin" />
                                            Rendering Look
                                        </>
                                    ) : (
                                        "Try This On"
                                    )}
                                </button>
                            </div>
                        </div>

                        <div className="lg:col-span-7 p-4 md:p-8">
                            <div className="grid grid-cols-1 justify-items-center gap-4 xl:grid-cols-2">
                                <div className="w-full max-w-[26rem] border-2 border-black bg-white">
                                    <div className="border-b-2 border-black px-4 py-3 font-mono-ui text-[10px] uppercase tracking-[0.25em]">
                                        Your Upload
                                    </div>
                                    <div className="aspect-[4/5] bg-[#f7f7f7]">
                                        {tryOnPreview ? (
                                            <img
                                                src={tryOnPreview}
                                                alt="Uploaded preview"
                                                className="w-full h-full object-contain object-center"
                                            />
                                        ) : (
                                            <div className="flex h-full items-center justify-center px-6 text-center text-sm text-muted-foreground">
                                                Add a photo to preview it here before generating the try-on.
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="w-full max-w-[26rem] border-2 border-black bg-white">
                                    <div className="border-b-2 border-black px-4 py-3 font-mono-ui text-[10px] uppercase tracking-[0.25em]">
                                        AI Result
                                    </div>
                                    <div className="aspect-[4/5] bg-[#f7f7f7]">
                                        {tryOnResult ? (
                                            <img
                                                src={tryOnResult}
                                                alt={`${product.name} virtual try-on`}
                                                className="w-full h-full object-contain object-center"
                                                data-testid="virtual-try-on-result"
                                            />
                                        ) : (
                                            <div className="flex h-full items-center justify-center px-6 text-center text-sm text-muted-foreground">
                                                Your generated look will appear here once the upload and garment reference are processed.
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* Related */}
            {!product.is_voucher && related.length > 0 && (
                <section className="border-b-4 border-black">
                    <div className="border-b-2 border-black px-4 py-10 md:px-8">
                        <span className="font-mono-ui text-xs uppercase tracking-[0.25em] text-muted-foreground">
                            // 04
                        </span>
                        <h2 className="font-heading text-4xl md:text-6xl uppercase leading-none mt-1">
                            Pair It With
                        </h2>
                    </div>
                    <div className="grid grid-cols-2 lg:grid-cols-4">
                        {related.map((p, idx) => (
                            <div
                                key={p.id}
                                className={`${idx % 2 === 1 ? "border-l-2" : ""} ${
                                    idx % 4 !== 3 ? "lg:border-r-2" : ""
                                } border-black`}
                            >
                                <ProductCard product={p} />
                            </div>
                        ))}
                    </div>
                </section>
            )}
        </div>
    );
};

export default ProductDetail;
